import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, Check, X, RefreshCw } from 'lucide-react';
import { Table } from '@/components';
import { vendorApi } from '@/services/vendorApi';
import { VendorListItem } from '@/types/vendor';
import toast from 'react-hot-toast';

export const VendorManagement: React.FC = () => {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState<VendorListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterApproval, setFilterApproval] = useState<string>('');

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (filterStatus) params.status = filterStatus;
      if (filterApproval) params.approvalStatus = filterApproval;
      
      console.log('🔍 Fetching vendors with params:', params);
      const data = await vendorApi.getAllVendors(params);
      console.log('✅ Vendors fetched:', data);
      setVendors(data.vendors);
    } catch (error: any) {
      console.error('❌ Error fetching vendors:', error);
      toast.error(error.message || 'Vendor listesi yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
  }, [filterStatus, filterApproval]);

  const handleDelete = async (vendorId: string) => {
    if (!confirm('Bu vendor\'ı silmek istediğinize emin misiniz?')) return;
    
    try {
      await vendorApi.deleteVendor(vendorId);
      toast.success('Vendor silindi');
      fetchVendors();
    } catch (error: any) {
      toast.error(error.message || 'Vendor silinemedi');
    }
  };

  const handleApprove = async (vendorId: string) => {
    try {
      await vendorApi.approveVendor(vendorId);
      toast.success('Vendor onaylandı');
      fetchVendors();
    } catch (error: any) {
      toast.error(error.message || 'Vendor onaylanamadı');
    }
  };

  const handleReject = async (vendorId: string) => {
    const reason = prompt('Red nedeni:');
    if (!reason) return;
    
    try {
      await vendorApi.rejectVendor(vendorId, reason);
      toast.success('Vendor reddedildi');
      fetchVendors();
    } catch (error: any) {
      toast.error(error.message || 'Vendor reddedilemedi');
    }
  };

  const handleStatusChange = async (vendorId: string, newStatus: string) => {
    try {
      await vendorApi.updateVendorStatus(vendorId, newStatus);
      toast.success('Durum güncellendi');
      fetchVendors();
    } catch (error: any) {
      toast.error(error.message || 'Durum güncellenemedi');
    }
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      active: 'bg-green-100 text-green-700',
      pending: 'bg-yellow-100 text-yellow-700',
      inactive: 'bg-gray-100 text-gray-700',
      suspended: 'bg-red-100 text-red-700',
      rejected: 'bg-red-100 text-red-700',
    };
    const labels: Record<string, string> = {
      active: 'Aktif',
      pending: 'Beklemede',
      inactive: 'İnaktif',
      suspended: 'Askıda',
      rejected: 'Reddedildi',
    };
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${colors[status] || 'bg-gray-100 text-gray-700'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getApprovalBadge = (approvalStatus: string) => {
    const colors: Record<string, string> = {
      approved: 'bg-green-100 text-green-700',
      pending: 'bg-yellow-100 text-yellow-700',
      rejected: 'bg-red-100 text-red-700',
    };
    const labels: Record<string, string> = {
      approved: 'Onaylandı',
      pending: 'Bekliyor',
      rejected: 'Reddedildi',
    };
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${colors[approvalStatus] || 'bg-gray-100 text-gray-700'}`}>
        {labels[approvalStatus] || approvalStatus}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Vendor Yönetimi</h1>
          <p className="text-gray-600 mt-1">Tüm vendor'ları yönetin</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={fetchVendors}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition flex items-center gap-2"
          >
            <RefreshCw size={18} />
            Yenile
          </button>
          <button
            onClick={() => navigate('/admin/vendors/new')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
          >
            <Plus size={20} />
            Yeni Vendor
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'Toplam Vendor', value: vendors.length, color: 'blue' },
          { label: 'Aktif', value: vendors.filter(v => v.status === 'active').length, color: 'green' },
          { label: 'Onay Bekleyen', value: vendors.filter(v => v.approvalStatus === 'pending').length, color: 'yellow' },
          { label: 'Reddedilen', value: vendors.filter(v => v.approvalStatus === 'rejected').length, color: 'red' },
        ].map((stat, idx) => (
          <div key={idx} className="bg-white p-4 rounded-lg shadow">
            <p className="text-sm text-gray-600">{stat.label}</p>
            <p className="text-2xl font-bold text-gray-900 mt-2">{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow flex gap-4">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">Durum</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Tümü</option>
            <option value="active">Aktif</option>
            <option value="pending">Beklemede</option>
            <option value="inactive">İnaktif</option>
            <option value="suspended">Askıda</option>
          </select>
        </div>
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">Onay Durumu</label>
          <select
            value={filterApproval}
            onChange={(e) => setFilterApproval(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Tümü</option>
            <option value="approved">Onaylandı</option>
            <option value="pending">Bekliyor</option>
            <option value="rejected">Reddedildi</option>
          </select>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : vendors.length === 0 ? (
        <div className="bg-white p-12 rounded-lg shadow text-center">
          <p className="text-gray-500">Henüz vendor kaydı yok</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vendor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  İletişim
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Komisyon
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Durum
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Onay
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  İşlemler
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {vendors.map(vendor => (
                <tr key={vendor.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {vendor.firstName} {vendor.lastName}
                      </div>
                      {vendor.companyName && (
                        <div className="text-sm text-gray-500">{vendor.companyName}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{vendor.email}</div>
                    <div className="text-sm text-gray-500">{vendor.phone}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">%{vendor.commissionRate}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(vendor.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getApprovalBadge(vendor.approvalStatus)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      {vendor.approvalStatus === 'pending' && (
                        <>
                          <button
                            onClick={() => handleApprove(vendor.id)}
                            className="p-2 text-green-600 hover:bg-green-100 rounded transition"
                            title="Onayla"
                          >
                            <Check size={16} />
                          </button>
                          <button
                            onClick={() => handleReject(vendor.id)}
                            className="p-2 text-red-600 hover:bg-red-100 rounded transition"
                            title="Reddet"
                          >
                            <X size={16} />
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => navigate(`/admin/vendors/${vendor.id}/edit`)}
                        className="p-2 text-blue-600 hover:bg-blue-100 rounded transition"
                        title="Düzenle"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(vendor.id)}
                        className="p-2 text-red-600 hover:bg-red-100 rounded transition"
                        title="Sil"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

